import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import {
  AppLink,
  DataTable,
  cx,
  dataColumns,
  formatCost,
  formatDateTime,
  useNarrow,
  type DataColumn,
  type DataGroup,
  type VisibilityColumn,
} from "@amber/ui";
import {
  DOWNLOAD_PROBLEM,
  backlogHref,
  platformEntry,
  type SourceHealthKey,
  type SourceRow,
  type SourceValues,
  type SourcesResponse,
} from "../../contracts/index";
import { canSelectSource, type SourceSelection } from "./bulk";
import {
  CoverageTimeline,
  Monogram,
  PlatformCode,
  SourceHealthCell,
  TimelineAxis,
  nextScanText,
  shortDate,
} from "./SourceVisuals";
import { SourceRowMenu } from "./SourceRowMenu";

type GroupKey = "needs_attention" | "unusually_quiet" | "healthy" | "paused" | "not_in_any_plan" | "not_collected";
const SOURCE_GROUPS: readonly { key: GroupKey; label: string; note: string; states: readonly SourceHealthKey[] }[] = [
  {
    key: "needs_attention",
    label: "Needs attention",
    note: "Failing, then partial, then late",
    states: ["failing", "partial", "late"],
  },
  {
    key: "unusually_quiet",
    label: "Unusually quiet",
    note: "Scans succeed, but nothing new for much longer than usual",
    states: ["unusually_quiet"],
  },
  { key: "healthy", label: "Healthy", note: "Sources with open gaps first", states: ["healthy"] },
  { key: "paused", label: "Paused", note: "Most recently paused first", states: ["paused"] },
  { key: "not_in_any_plan", label: "Not in any plan", note: "Not scanned; not a problem", states: ["not_in_any_plan"] },
  {
    key: "not_collected",
    label: "Not collected yet",
    note: "Known accounts on platforms we don't collect",
    states: ["not_collected"],
  },
];

const col = dataColumns<SourceRow>();

type SourceValue = SourceValues["sources"][number];

interface ColumnContext {
  readonly data: SourcesResponse;
  readonly narrow: boolean;
  readonly values: ReadonlyMap<string, SourceValue> | null;
  readonly onGaps: (sourceId: string) => void;
  readonly onEdit: (sourceId: string) => void;
}

export const SOURCE_VISIBILITY: readonly VisibilityColumn[] = [
  { id: "source", label: "Source", canHide: false },
  { id: "people", label: "Person(s)" },
  { id: "platform", label: "Platform" },
  { id: "health", label: "Health" },
  { id: "coverage", label: "Coverage" },
  { id: "complete", label: "Complete through" },
  { id: "last_scan", label: "Last scan" },
  { id: "next_scan", label: "Next scan" },
  { id: "items_7d", label: "Items (7 days)" },
  { id: "items_total", label: "Collected · all time" },
  { id: "problems", label: "Download problems" },
  { id: "gaps", label: "Open gaps" },
  { id: "backfill", label: "Backfill" },
  { id: "eligible", label: "Eligible / month", hiddenByDefault: true },
  { id: "cost", label: "Cost per eligible", hiddenByDefault: true },
  { id: "kind", label: "Kind" },
  { id: "cadence", label: "Cadence" },
];

export const VALUE_COLUMNS = ["eligible", "cost"];
function sourceColumns({ data, narrow, values, onGaps, onEdit }: ColumnContext): readonly DataColumn<SourceRow>[] {
  const lead: readonly DataColumn<SourceRow>[] = [
    col.display({
      id: "source",
      header: "Source",
      meta: { width: narrow ? 200 : 240 },
      cell: ({ row: { original: r } }) => <SourceName r={r} />,
    }),
  ];
  const health: readonly DataColumn<SourceRow>[] = [
    col.display({
      id: "health",
      header: "Health",
      meta: { width: 150, interactive: true, cellClassName: "overflow-visible" },
      cell: ({ row }) => <SourceHealthCell row={row.original} />,
    }),
    col.display({
      id: "coverage",
      header: () => (
        <span className="flex w-full flex-col gap-0.5 py-1">
          <span>
            Coverage <span className="text-xs font-normal">· last {data.window.days} days</span>
          </span>
          <TimelineAxis from={data.window.from} to={data.window.to} />
        </span>
      ),
      meta: { width: narrow ? 180 : 260, interactive: true, cellClassName: "overflow-visible" },
      cell: ({ row: { original: r } }) => (
        <CoverageTimeline
          row={r}
          from={data.window.from}
          to={data.window.to}
          onGap={() => {
            onGaps(r.id);
          }}
        />
      ),
    }),
    col.display({
      id: "complete",
      header: "Complete through",
      meta: { width: 140 },
      cell: ({ row: { original: r } }) => <CompleteThrough row={r} />,
    }),
  ];
  const menu = col.display({
    id: "actions",
    header: () => <span className="sr-only">Source actions</span>,
    meta: { width: 48, label: "Source actions", interactive: true, cellClassName: "overflow-visible" },
    cell: ({ row }) => <SourceRowMenu row={row.original} edit={data.actions.edit_source} onEdit={onEdit} />,
  });
  if (narrow) return [...lead, ...health, menu];
  return [...lead, ...OWNER_COLUMNS, ...health, ...scanColumns(onGaps), ...valueColumns(values), ...TAIL_COLUMNS, menu];
}

// DI-121: during a history extension the row also says how far back it is complete, and whether it waits.
function CompleteThrough({ row }: { row: SourceRow }) {
  return (
    <>
      <span className="font-mono text-md text-fg">{shortDate(row.complete_through)}</span>
      {row.extension && row.complete_back_to ? (
        <span
          className={cx("block truncate text-sm", row.extension.waiting ? "text-muted" : "text-link")}
          title={`Complete back to ${shortDate(row.complete_back_to)}. History extension: ${row.extension.waiting ?? `reading back to ${shortDate(row.extension.back_to)}`}`}
        >
          back to {shortDate(row.complete_back_to)}
        </span>
      ) : null}
    </>
  );
}

function BackfillCell({ row }: { row: SourceRow }) {
  if (!row.backfill) return "";
  const { days_read: read, days_total: total } = row.backfill;
  const pct = total > 0 ? Math.min(100, Math.round((read / total) * 100)) : 0;
  return (
    <span className="flex items-center gap-1.5">
      <span aria-hidden="true" className="h-1.5 w-10 flex-none overflow-hidden rounded-full bg-surface-3">
        <span className="block h-full rounded-full bg-link" style={{ width: `${String(pct)}%` }} />
      </span>
      <span className="truncate text-sm text-fg">
        {Math.round(read)} of {Math.round(total)} days read
      </span>
    </span>
  );
}

function valueColumns(values: ReadonlyMap<string, SourceValue> | null): readonly DataColumn<SourceRow>[] {
  const valueOf = (r: SourceRow) => values?.get(r.id);
  return [
    col.display({
      id: "eligible",
      header: "Eligible / month",
      meta: { width: 130 },
      cell: ({ row: { original: r } }) => {
        const v = valueOf(r);
        if (!v) return values ? "—" : "";
        return (
          <span title="Eligible communications from this source in the last 30 days, and how many only from here">
            <span className="font-mono text-fg">{v.eligible}</span>{" "}
            <span className="text-sm text-muted">{v.only_here} only here</span>
          </span>
        );
      },
    }),
    col.display({
      id: "cost",
      header: "Cost per eligible",
      meta: { width: 120 },
      cell: ({ row: { original: r } }) => {
        const v = valueOf(r);
        if (!v) return values ? "—" : "";
        if (v.per_eligible_usd === null) return <span className="text-muted">none eligible</span>;
        return <span className="font-mono text-fg">{formatCost(String(v.per_eligible_usd))}</span>;
      },
    }),
  ];
}

const TAIL_COLUMNS: readonly DataColumn<SourceRow>[] = [
  col.display({
    id: "kind",
    header: "Kind",
    meta: { width: 150 },
    cell: ({ row }) => row.original.kind_label,
  }),
  col.display({
    id: "cadence",
    header: "Cadence",
    meta: { width: 150 },
    cell: ({ row }) => row.original.tier_label,
  }),
];

function SourceName({ r }: { r: SourceRow }) {
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <b className="truncate font-strong text-fg" title={r.title}>
        {r.title}
      </b>
      <a
        href={r.url}
        target="_blank"
        rel="noreferrer noopener"
        aria-label={`Open ${r.title} on ${r.platform.label}`}
        className="flex-none text-muted hover:text-link"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <ExternalLink size={12} aria-hidden="true" />
      </a>
    </span>
  );
}

function PeopleCell({ row }: { row: SourceRow }) {
  if (row.people.length > 0) {
    return (
      <span className="flex">
        {row.people.map((p) => (
          <Monogram key={p.id} name={p.name} />
        ))}
      </span>
    );
  }
  if (row.shared_plan) return <span className="text-md">Shared-source plan</span>;
  return "—";
}

const OWNER_COLUMNS: readonly DataColumn<SourceRow>[] = [
  col.display({
    id: "people",
    header: "Person(s)",
    meta: { width: 130 },
    cell: ({ row: { original: r } }) => (
      <span className="flex items-center gap-1.5">
        <PeopleCell row={r} />
        {r.required ? (
          <span className="rounded-full border border-divider px-1.5 text-2xs leading-4 text-muted">Required</span>
        ) : null}
      </span>
    ),
  }),
  col.display({
    id: "platform",
    header: "Platform",
    meta: { width: 120 },
    cell: ({ row: { original: r } }) => (
      <span className="inline-flex items-center gap-1.5">
        <PlatformCode code={platformEntry(r.platform.key)?.code ?? r.platform.key} />
        <span className="truncate text-fg">{r.platform.label}</span>
      </span>
    ),
  }),
];

function scanColumns(onGaps: (sourceId: string) => void): readonly DataColumn<SourceRow>[] {
  return [
    col.display({
      id: "last_scan",
      header: "Last scan",
      meta: { width: 130 },
      cell: ({ row: { original: r } }) =>
        r.last_scan ? <span title={formatDateTime(r.last_scan.at)}>{r.last_scan.label}</span> : "—",
    }),
    col.display({
      id: "next_scan",
      header: "Next scan",
      meta: { width: 110 },
      cell: ({ row: { original: r } }) => (
        <span
          className={cx(
            r.next_scan.kind === "scanning" && "text-link",
            r.next_scan.kind === "at" && r.next_scan.late && "text-waiting",
          )}
        >
          {nextScanText(r)}
        </span>
      ),
    }),
    col.display({
      id: "items_7d",
      header: "Items (7 days)",
      meta: { width: 80 },
      cell: ({ row: { original: r } }) =>
        r.items_7d > 0 ? (
          <AppLink
            href={backlogHref({ source: r.id })}
            className="text-link"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {r.items_7d}
          </AppLink>
        ) : (
          "—"
        ),
    }),
    col.display({
      id: "items_total",
      header: "Collected · all time",
      meta: { width: 100 },
      cell: ({ row: { original: r } }) =>
        r.items_total > 0 ? <span className="font-mono text-fg">{r.items_total}</span> : "—",
    }),
    col.display({
      id: "problems",
      header: "Download problems",
      meta: { width: 90 },
      cell: ({ row: { original: r } }) =>
        r.download_problems > 0 ? (
          <AppLink
            href={backlogHref({
              source: r.id,
              stage: DOWNLOAD_PROBLEM.stage.join(","),
              status: DOWNLOAD_PROBLEM.status.join(","),
            })}
            className="text-waiting"
            aria-label={`${String(r.download_problems)} download problems`}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {r.download_problems}
          </AppLink>
        ) : (
          ""
        ),
    }),
    col.display({
      id: "gaps",
      header: "Open gaps",
      meta: { width: 70 },
      cell: ({ row: { original: r } }) =>
        r.open_gaps > 0 ? (
          <button
            type="button"
            className="font-strong text-link underline"
            onClick={(e) => {
              e.stopPropagation();
              onGaps(r.id);
            }}
          >
            {r.open_gaps}
          </button>
        ) : (
          ""
        ),
    }),
    col.display({
      id: "backfill",
      header: "Backfill",
      meta: { width: 180 },
      cell: ({ row }) => <BackfillCell row={row.original} />,
    }),
  ];
}

const PINNED = ["source"];
const SELECT_WIDTH = 36;

function widthOf(columns: readonly DataColumn<SourceRow>[], hidden: ReadonlySet<string>): number {
  return columns.reduce((sum, c) => (c.id && hidden.has(c.id) ? sum : sum + (c.meta?.width ?? 0)), SELECT_WIDTH);
}

function isMuted(r: SourceRow): boolean {
  return r.health.state === "not_collected" || r.health.state === "not_in_any_plan";
}

function rowLabel(row: SourceRow): string {
  return row.title;
}

export function SourcesTable({
  data,
  rows,
  selected,
  grouped,
  hidden,
  values,
  selection,
  onOpen,
  onGaps,
  onEdit,
}: {
  data: SourcesResponse;
  rows: readonly SourceRow[];
  selected: string | null;
  grouped: boolean;
  hidden: ReadonlySet<string>;
  values: ReadonlyMap<string, SourceValue> | null;
  selection: SourceSelection;
  onOpen: (id: string) => void;
  onGaps: (sourceId: string) => void;
  onEdit: (sourceId: string) => void;
}) {
  const [collapsed, setCollapsed] = useState<ReadonlySet<GroupKey>>(
    () => new Set(["not_in_any_plan", "not_collected"]),
  );
  const narrow = useNarrow();
  const columns = sourceColumns({ data, narrow, values, onGaps, onEdit });
  const groups = grouped
    ? SOURCE_GROUPS.map((g): DataGroup<SourceRow> => {
        const members = rows.filter((r) => g.states.includes(r.health.state));
        return {
          key: g.key,
          label: g.label,
          note: g.note,
          rows: members,
          count: members.length,
          collapsed: collapsed.has(g.key),
          attention: g.key === "needs_attention",
          onToggle: () => {
            const next = new Set(collapsed);
            if (next.has(g.key)) next.delete(g.key);
            else next.add(g.key);
            setCollapsed(next);
          },
        };
      }).filter((g) => g.count > 0)
    : null;
  const selectedGroup = SOURCE_GROUPS.find((g) =>
    g.states.includes(rows.find((r) => r.id === selected)?.health.state ?? "healthy"),
  );
  useEffect(() => {
    if (selectedGroup && collapsed.has(selectedGroup.key))
      setCollapsed(new Set([...collapsed].filter((k) => k !== selectedGroup.key)));
  }, [selectedGroup, collapsed]);
  return (
    <DataTable
      label="Sources"
      columns={columns}
      rows={rows}
      rowId={(r) => r.id}
      minWidth={narrow ? 648 : widthOf(columns, hidden)}
      pinned={PINNED}
      hidden={hidden}
      selection={{
        picked: selection.picked,
        onToggle: selection.toggle,
        onToggleLoaded: selection.toggleLoaded,
        rowLabel,
        canSelect: canSelectSource,
      }}
      groups={groups}
      selectedId={selected}
      empty="No sources match these filters."
      rowOptions={(r) => ({
        label: `${r.title}, ${r.health.label}`,
        muted: isMuted(r),
        onOpen: () => {
          onOpen(r.id);
        },
      })}
    />
  );
}
